import apiClient from '@/shared/api/client';
import type { ApiResponse } from '@/shared/api/types';
import type { MembershipPlan } from '@/modules/strategy/api/extractionApi';

export type { MembershipPlan };

export type PipelineStageStatus = 'pending' | 'in_progress' | 'completed' | 'failed';
export type LocationPipelineStatus = Partial<
  Record<
    'crawl' | 'extraction' | 'audit' | 'competitors' | 'questionnaire' | 'gapAnalysis',
    PipelineStageStatus
  >
>;

export interface LocationData {
  id: string;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  website?: string;
  /** IANA zone every local-time feature uses (visit times, agent schedules). */
  timezone?: string;
  /** The zone the address is in, when the address has been placed on a map (read-only). */
  addressTimezone?: string | null;
  isDefault: boolean;
  status: string;
  pipelineStatus?: LocationPipelineStatus | null;
}

export type LocationInput = Omit<LocationData, 'id' | 'status' | 'isDefault' | 'addressTimezone'>;

export interface DayHours {
  open: string;
  close: string;
}

export interface LocationProfile {
  id: string;
  locationId: string;
  services: string[];
  providers: string[];
  technology: string[];
  insurances: string[];
  languages: string[];
  membershipPlans: MembershipPlan[] | null;
  paymentOptions: string[] | null;
  officeHours: Record<string, DayHours | null> | null;
}

export type LocationProfileInput = Omit<LocationProfile, 'id' | 'locationId'>;

export const locationsApi = {
  getAll: async (): Promise<ApiResponse<LocationData[]>> => {
    const { data } = await apiClient.get<ApiResponse<LocationData[]>>('/locations');
    return data;
  },

  update: async (
    id: string,
    payload: Partial<LocationInput>,
  ): Promise<ApiResponse<LocationData>> => {
    const { data } = await apiClient.put<ApiResponse<LocationData>>(`/locations/${id}`, payload);
    return data;
  },

  create: async (payload: LocationInput): Promise<ApiResponse<LocationData>> => {
    const { data } = await apiClient.post<ApiResponse<LocationData>>('/locations', payload);
    return data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/locations/${id}`);
  },

  getProfile: async (id: string): Promise<ApiResponse<LocationProfile | null>> => {
    const { data } = await apiClient.get<ApiResponse<LocationProfile | null>>(
      `/locations/${id}/profile`,
    );
    return data;
  },

  updateProfile: async (
    id: string,
    payload: LocationProfileInput,
  ): Promise<ApiResponse<LocationProfile>> => {
    const { data } = await apiClient.put<ApiResponse<LocationProfile>>(
      `/locations/${id}/profile`,
      payload,
    );
    return data;
  },

  patchProfile: async (
    id: string,
    payload: Partial<LocationProfileInput>,
  ): Promise<ApiResponse<LocationProfile>> => {
    const { data } = await apiClient.put<ApiResponse<LocationProfile>>(
      `/locations/${id}/profile`,
      payload,
    );
    return data;
  },

  restartPipeline: async (id: string, mode: 'retry' | 'restart'): Promise<ApiResponse<null>> => {
    const { data } = await apiClient.post<ApiResponse<null>>(`/locations/${id}/restart-pipeline`, {
      mode,
    });
    return data;
  },
};
